import React, { useEffect, useState } from "react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";

export default function DoctorProfile() {
  const [doctor, setDoctor] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/doctor/me");
      setDoctor(data);
      setForm({
        name: data.name || "",
        specialty: data.specialty || "",
        qualification: data.qualification || "",
        experience_years: data.experience_years || 0,
        city: data.city || "",
        hospital_affiliation: data.hospital_affiliation || "",
        bio: data.bio || "",
        registration_number: data.registration_number || "",
        languages: (data.languages || []).join(", "),
        consultation_fee: data.consultation_fee || 500,
      });
    })();
  }, []);

  const setField = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        experience_years: Number(form.experience_years) || 0,
        consultation_fee: Number(form.consultation_fee) || 0,
        languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.put("/doctor/me", payload);
      setDoctor(data);
      toast.success("Profile updated");
    } catch {
      toast.error("Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  if (!doctor) return <AppShell pageTitle="Profile"><div className="text-sm text-muted-foreground">Loading…</div></AppShell>;

  return (
    <AppShell pageTitle="My profile" pageDescription="Verified credentials build trust with patients and peers.">
      <div className="max-w-4xl grid grid-cols-1 lg:grid-cols-2 gap-4 rounded-lg border border-border bg-white p-6 lg:p-8" data-testid="doctor-profile-form">
        <Field label="Full name"><Input value={form.name} onChange={setField("name")} data-testid="profile-name-input" /></Field>
        <Field label="Specialty"><Input value={form.specialty} onChange={setField("specialty")} data-testid="profile-specialty-input" /></Field>
        <Field label="Qualification"><Input value={form.qualification} onChange={setField("qualification")} data-testid="profile-qualification-input" /></Field>
        <Field label="Experience (years)"><Input type="number" value={form.experience_years} onChange={setField("experience_years")} data-testid="profile-experience-input" /></Field>
        <Field label="City"><Input value={form.city} onChange={setField("city")} data-testid="profile-city-input" /></Field>
        <Field label="Hospital affiliation"><Input value={form.hospital_affiliation} onChange={setField("hospital_affiliation")} data-testid="profile-hospital-input" /></Field>
        <Field label="Registration No."><Input value={form.registration_number} onChange={setField("registration_number")} data-testid="profile-registration-input" /></Field>
        <Field label="Languages (comma-separated)"><Input value={form.languages} onChange={setField("languages")} data-testid="profile-languages-input" /></Field>
        <Field label="Consultation fee (₹)"><Input type="number" value={form.consultation_fee} onChange={setField("consultation_fee")} data-testid="profile-fee-input" /></Field>
        <div className="lg:col-span-2">
          <Label>Bio</Label>
          <Textarea value={form.bio} onChange={setField("bio")} rows={4} data-testid="profile-bio-input" />
        </div>
        <div className="lg:col-span-2">
          <Button onClick={save} disabled={saving} className="w-full lg:w-auto" data-testid="profile-save-button">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </div>
      </div>
    </AppShell>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label>{label}</Label>
      {children}
    </div>
  );
}
